import React, { useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { theme } from "../../../theme/colors";
import { BUTTON_SIZES, BUTTON_VARIANTS, prefersReducedMotion } from "./buttonTokens";
import useGlassButtonShader from "./useGlassButtonShader";

/**
 * CivixButton — branded button primitive.
 *
 * The `primary` variant is the glassy emerald CTA baseline from Join /compare plans:
 * smooth gradient, cursor spotlight, and glass overlay.
 */
export default function CivixButton({
  children,
  label,
  variant = "primary",
  size = "md",
  component = "button",
  disabled = false,
  loading = false,
  fullWidth = false,
  sx = {},
  ...props
}) {
  const rootRef = useRef(null);
  const sizeTokens = BUTTON_SIZES[size] || BUTTON_SIZES.md;
  const variantTokens = BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary;
  const reducedMotion = prefersReducedMotion();
  const useGlass = variantTokens.glass && !disabled && !loading && !reducedMotion;

  const { canvasRef, updatePointer, handleMouseLeave, active } = useGlassButtonShader({
    enabled: useGlass,
    rootRef,
    shaderPalette: variantTokens.shader,
  });

  const content = children ?? label;

  const handleMouseMove = (e) => {
    if (active) updatePointer(e.clientX, e.clientY);
    props.onMouseMove?.(e);
  };

  const handleLeave = (e) => {
    if (active) handleMouseLeave();
    props.onMouseLeave?.(e);
  };

  return (
    <Box
      ref={rootRef}
      component={component}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onMouseMove={active ? handleMouseMove : props.onMouseMove}
      onMouseLeave={active ? handleLeave : props.onMouseLeave}
      {...props}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        width: fullWidth ? "100%" : "auto",
        minHeight: sizeTokens.minHeight,
        px: sizeTokens.px,
        py: sizeTokens.py,
        borderRadius: sizeTokens.borderRadius,
        border: variantTokens.border,
        textDecoration: "none",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        fontWeight: 700,
        fontSize: sizeTokens.fontSize,
        fontFamily: "inherit",
        color: variantTokens.color,
        bgcolor: variantTokens.bgcolor,
        ...(variantTokens.background ? { background: variantTokens.background } : {}),
        boxShadow: variantTokens.boxShadow,
        outline: "none",
        overflow: variantTokens.glass ? "hidden" : "visible",
        isolation: "isolate",
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled || loading ? "none" : "auto",
        transition: "transform 0.12s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
        "&:hover": disabled || loading
          ? {}
          : {
              boxShadow: variantTokens.boxShadowHover,
              borderColor: variantTokens.borderHover,
              ...variantTokens["&:hover"],
            },
        "&:active": disabled || loading
          ? {}
          : {
              transform: "scale(0.97)",
            },
        "&:focus-visible": {
          boxShadow: theme.joinFocusRing,
        },
        ...sx,
      }}
    >
      {active && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "block",
            }}
          />
        </Box>
      )}

      {variantTokens.glassOverlay && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: variantTokens.glassOverlay,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}

      <Box
        component="span"
        sx={{
          position: "relative",
          zIndex: 2,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          letterSpacing: "0.01em",
        }}
      >
        {loading && (
          <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} aria-hidden />
        )}
        {content}
      </Box>
    </Box>
  );
}
